import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useToast } from '@shared/hooks/use-toast';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Entry } from '../../../models';
import { useCancelEntry } from '../../hooks/useCancelEntry';

// Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithEntry: (entry: Entry) => void;
}

const CancelEntryModal = forwardRef<ExtendedModalCenterRef, {}>((_props, ref) => {
  // Hook de traducciones
  const { t } = useTranslation('merchandiseV3');
  const { toast } = useToast();
  const { canAccess } = usePermissions();
  const cancelEntryMutation = useCancelEntry();

  // Verificar permisos
  const canCancel = canAccess(['tenant', 'tenant_3pl'], ['merchandises.cancel']);

  // Referencia interna al ModalCenter para evitar recursión
  const internalModalRef = useRef<ModalCenterRef>(null);

  // Estado interno del modal - NO causa re-render del padre
  const [entry, setEntry] = useState<Entry | null>(null);

  const resetForm = () => {
    setEntry(null);
  };

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const handleConfirmCancel = async () => {
    try {
      // Verificar permisos antes de cancelar
      if (!canCancel) {
        toast({
          title: t('modal.cancel.error_title', 'Error al cancelar'),
          description: t('modal.cancel.error_no_permission', 'No tienes permisos para cancelar entradas'),
          variant: 'destructive',
        });
        return;
      }

      if (!entry) {
        toast({
          title: t('modal.cancel.error_no_selection', 'Error'),
          description: t('modal.cancel.error_no_selection_description', 'No se ha seleccionado ninguna entrada'),
          variant: 'destructive',
        });
        return;
      }

      // Llamar al hook de cancelación
      await cancelEntryMutation.mutateAsync(entry.id);

      // Toast de éxito
      toast({
        title: t('modal.cancel.success_title', 'Entrada cancelada'),
        description: t('modal.cancel.success_description', 'La entrada {{reference}} ha sido cancelada correctamente', {
          reference: entry.reference,
        }),
        variant: 'default',
      });

      // Solo cerrar modal si fue exitoso
      resetForm();
      handleCancel();
    } catch (error: any) {
      // Toast de error
      let errorMessage = t('modal.cancel.error_description', 'Error al cancelar la entrada');

      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        title: t('modal.cancel.error_title', 'Error al cancelar'),
        description: errorMessage,
        variant: 'destructive',
      });
      console.error('Error canceling entry:', error);
    }
  };

  // ÚNICO useCallback necesario - Se pasa a useImperativeHandle
  const openWithEntry = useCallback((entryData: Entry) => {
    setEntry(entryData);
    internalModalRef.current?.openModal();
  }, []);

  // Exponer métodos personalizados usando useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithEntry, // ← Método nuevo para uso directo
    }),
    [openWithEntry],
  );

  const isLoading = cancelEntryMutation.isPending;

  return (
    <ModalCenter ref={internalModalRef} title={t('modal.cancel.title', 'Cancelar entrada')} type="error">
      <div className="space-y-4">
        <p className="text-sm text-text">
          {t('modal.cancel.confirmation', '¿Estás seguro de que deseas cancelar la entrada')}{' '}
          <strong>{entry?.reference}</strong>?
        </p>
        <p className="text-sm text-text">
          {t(
            'modal.cancel.confirmation_subtitle',
            'Esta acción no se puede deshacer. Solo se pueden cancelar entradas en estado pendiente.',
          )}
        </p>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="lg"
            onClick={handleCancel}
            name="cancel"
            className="w-full sm:w-auto"
            disabled={isLoading}
          >
            {t('button.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            name="cancel-entry-btn-confirm"
            onClick={handleConfirmCancel}
            className="w-full sm:w-auto"
            disabled={isLoading}
            data-testid={`cancel-entry-button-${entry?.id}`}
          >
            {isLoading ? t('button.canceling', 'Cancelando...') : t('button.confirm_cancel', 'Confirmar cancelación')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

CancelEntryModal.displayName = 'CancelEntryModal';

export default CancelEntryModal;
